import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'


import { get, post, patch, del } from '@/lib/api'

import type { Project } from './use-projects'
import type { FunctionRole, DeveloperGroup, PaginationMeta } from '@company/shared'

// ==================== Types ====================

export type TeamStatus = 'active' | 'archived'
export type TeamMemberType = 'developer' | 'function'



export interface Team {
  id: string
  name: string
  description: string | null
  leaderId: string
  status: TeamStatus
  createdAt: string
  updatedAt: string | null
  deletedAt: string | null
}

export interface TeamMember {
  id: string
  teamId: string
  employeeId: string
  memberType: TeamMemberType
  functionRole: FunctionRole | null
  developerGroup: DeveloperGroup | null
  isLeader: boolean
  joinedAt: string
  createdAt: string
  updatedAt: string | null
  deletedAt: string | null
  employee?: {
    id: string
    name: string
    email: string
    avatar?: string | null
    role?: string
  }
}

export interface GroupedTeamMembers {
  leader: TeamMember | null
  developers: Record<DeveloperGroup, TeamMember[]>
  functions: Record<FunctionRole, TeamMember[]>
}

export interface TeamStats {
  memberCount: number
  projectCount: number
  totalHours: number
  taskStats: {
    total: number
    completed: number
    inProgress: number
    completionRate: number
  }
  memberHours: Array<{
    employeeId: string
    employeeName: string
    hours: number
  }>
}

export interface TeamListParams {
  status?: TeamStatus
  search?: string
  page?: number
  pageSize?: number
}

export interface CreateTeamInput {
  name: string
  description?: string
}

export interface UpdateTeamInput {
  name?: string
  description?: string
}

export interface AddTeamMemberInput {
  employeeId: string
  memberType: TeamMemberType
  functionRole?: FunctionRole
  developerGroup?: DeveloperGroup
}

export interface UpdateTeamMemberInput {
  developerGroup?: DeveloperGroup
}

export interface CreateTeamProjectInput {
  name: string
  description?: string
  startDate?: string
  endDate?: string
}

// ==================== Query Keys ====================

export const teamKeys = {
  all: ['teams'] as const,
  lists: () => [...teamKeys.all, 'list'] as const,
  list: (params: TeamListParams) => [...teamKeys.lists(), params] as const,
  details: () => [...teamKeys.all, 'detail'] as const,
  detail: (id: string) => [...teamKeys.details(), id] as const,
  members: (id: string) => [...teamKeys.detail(id), 'members'] as const,
  stats: (id: string) => [...teamKeys.detail(id), 'stats'] as const,
  projects: (id: string) => [...teamKeys.detail(id), 'projects'] as const,
}

// ==================== Hooks ====================

/**
 * 获取团队列表
 */
export function useTeams(params: TeamListParams = {}) {
  return useQuery({
    queryKey: teamKeys.list(params),
    queryFn: () => get<Team[]>('/teams', params as Record<string, unknown>),
  })
}

/**
 * 获取团队列表（带分页元数据）
 */
export function useTeamsWithMeta(params: TeamListParams = {}) {
  return useQuery({
    queryKey: teamKeys.list(params),
    queryFn: () => get<{ data: Team[]; meta: PaginationMeta }>('/teams', params as Record<string, unknown>),
  })
}

/**
 * 获取单个团队
 */
export function useTeam(id: string) {
  return useQuery({
    queryKey: teamKeys.detail(id),
    queryFn: () => get<Team>(`/teams/${id}`),
    enabled: !!id,
  })
}

/**
 * 创建团队
 */
export function useCreateTeam() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: CreateTeamInput) => post<Team>('/teams', data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: teamKeys.lists() })
    },
  })
}

/**
 * 更新团队
 */
export function useUpdateTeam() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateTeamInput }) =>
      patch<Team>(`/teams/${id}`, data),
    onSuccess: (_, { id }) => {
      void queryClient.invalidateQueries({ queryKey: teamKeys.detail(id) })
      void queryClient.invalidateQueries({ queryKey: teamKeys.lists() })
    },
  })
}

/**
 * 归档团队
 */
export function useArchiveTeam() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => post<null>(`/teams/${id}/archive`),
    onSuccess: (_, id) => {
      void queryClient.invalidateQueries({ queryKey: teamKeys.detail(id) })
      void queryClient.invalidateQueries({ queryKey: teamKeys.lists() })
    },
  })
}

/**
 * 获取团队成员
 */
export function useTeamMembers(teamId: string) {
  return useQuery({
    queryKey: teamKeys.members(teamId),
    queryFn: () => get<GroupedTeamMembers>(`/teams/${teamId}/members`),
    enabled: !!teamId,
  })
}

/**
 * 添加团队成员
 */
export function useAddTeamMember() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ teamId, data }: { teamId: string; data: AddTeamMemberInput }) =>
      post<TeamMember>(`/teams/${teamId}/members`, data),
    onSuccess: (_, { teamId }) => {
      void queryClient.invalidateQueries({ queryKey: teamKeys.members(teamId) })
      void queryClient.invalidateQueries({ queryKey: teamKeys.stats(teamId) })
    },
  })
}

/**
 * 移除团队成员
 */
export function useRemoveTeamMember() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ teamId, memberId }: { teamId: string; memberId: string }) =>
      del<null>(`/teams/${teamId}/members/${memberId}`),
    onSuccess: (_, { teamId }) => {
      void queryClient.invalidateQueries({ queryKey: teamKeys.members(teamId) })
      void queryClient.invalidateQueries({ queryKey: teamKeys.stats(teamId) })
    },
  })
}

/**
 * 更新团队成员
 */
export function useUpdateTeamMember() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ teamId, memberId, data }: { teamId: string; memberId: string; data: UpdateTeamMemberInput }) =>
      patch<TeamMember>(`/teams/${teamId}/members/${memberId}`, data),
    onSuccess: (_, { teamId }) => {
      void queryClient.invalidateQueries({ queryKey: teamKeys.members(teamId) })
    },
  })
}

/**
 * 设为团队负责人
 */
export function useSetTeamLeader() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ teamId, memberId }: { teamId: string; memberId: string }) =>
      post<null>(`/teams/${teamId}/members/${memberId}/leader`),
    onSuccess: (_, { teamId }) => {
      void queryClient.invalidateQueries({ queryKey: teamKeys.detail(teamId) })
      void queryClient.invalidateQueries({ queryKey: teamKeys.members(teamId) })
    },
  })
}

/**
 * 设为开发组组长
 */
export function useSetTeamGroupLeader() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ teamId, memberId }: { teamId: string; memberId: string }) =>
      post<null>(`/teams/${teamId}/members/${memberId}/group-leader`),
    onSuccess: (_, { teamId }) => {
      void queryClient.invalidateQueries({ queryKey: teamKeys.members(teamId) })
    },
  })
}

/**
 * 取消开发组组长
 */
export function useRemoveTeamGroupLeader() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ teamId, memberId }: { teamId: string; memberId: string }) =>
      del<null>(`/teams/${teamId}/members/${memberId}/group-leader`),
    onSuccess: (_, { teamId }) => {
      void queryClient.invalidateQueries({ queryKey: teamKeys.members(teamId) })
    },
  })
}

/**
 * 获取团队统计
 */
export function useTeamStats(teamId: string) {
  return useQuery({
    queryKey: teamKeys.stats(teamId),
    queryFn: () => get<TeamStats>(`/teams/${teamId}/stats`),
    enabled: !!teamId,
  })
}

/**
 * 获取团队项目列表
 */
export function useTeamProjects(teamId: string) {
  return useQuery({
    queryKey: teamKeys.projects(teamId),
    queryFn: () => get<Project[]>(`/teams/${teamId}/projects`),
    enabled: !!teamId,
  })
}

/**
 * 在团队下创建项目
 */
export function useCreateTeamProject() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ teamId, data }: { teamId: string; data: CreateTeamProjectInput }) =>
      post<Project>(`/teams/${teamId}/projects`, data),
    onSuccess: (_, { teamId }) => {
      void queryClient.invalidateQueries({ queryKey: teamKeys.projects(teamId) })
      void queryClient.invalidateQueries({ queryKey: teamKeys.stats(teamId) })
      // 同时刷新项目列表
      void queryClient.invalidateQueries({ queryKey: ['projects'] })
    },
  })
}
